/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Calculator, TrendingUp, AlertCircle, Loader2, Save, FileDown, CalendarClock, X } from "lucide-react";
import { toast } from "sonner";
import { accountingApi, adminApi, formatApiError } from "@/lib/api";
import { RentalAccountingDocs } from "@/components/admin/RentalAccountingDocs";

function fmtEur(v) { return Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 }) + " €"; }
function fmtDate(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; } }

export default function AdminAccounting() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rate, setRate] = useState(21.2);
  const [savingRate, setSavingRate] = useState(false);
  // Edit paid-at modal
  const [editing, setEditing] = useState(null); // holds {id, number}
  const [editDate, setEditDate] = useState("");
  const [saving, setSaving] = useState(false);

  const openEditDate = (inv) => {
    setEditDate((inv.paid_at || "").slice(0, 10));
    setEditing(inv);
  };
  const saveEditDate = async (e) => {
    e.preventDefault();
    if (!editDate) return;
    setSaving(true);
    try {
      await adminApi.setPaidAt(editing.id, { paid_at: editDate, status: "payee" });
      toast.success("Date d'encaissement mise à jour.");
      setEditing(null);
      const s = await accountingApi.summary(year); setSummary(s);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([accountingApi.summary(year), accountingApi.getSettings()])
      .then(([s, set]) => { setSummary(s); setRate(set.urssaf_rate); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [year]);

  const saveRate = async () => {
    setSavingRate(true);
    try {
      await accountingApi.updateSettings({ urssaf_rate: Number(rate) });
      toast.success("Taux URSSAF mis à jour.");
      const s = await accountingApi.summary(year); setSummary(s);
    } catch (e) { toast.error(formatApiError(e)); }
    setSavingRate(false);
  };

  const exportCsv = () => {
    if (!summary) return;
    const rows = [["Trimestre", "Période", "Déclaration", "CA TTC", "URSSAF dû", "Net après URSSAF", "Nb factures"]];
    summary.quarters.forEach((q) => {
      rows.push([`T${q.quarter}`, q.label, `${fmtDate(q.declaration_window.start)} → ${fmtDate(q.declaration_window.end)}`,
                 q.ca_ttc.toFixed(2).replace(".", ","), q.urssaf_due.toFixed(2).replace(".", ","),
                 q.net_after_urssaf.toFixed(2).replace(".", ","), q.invoice_count]);
    });
    rows.push(["TOTAL", `Année ${summary.year}`, "", summary.totals.ca_ttc.toFixed(2).replace(".", ","), summary.totals.urssaf_due.toFixed(2).replace(".", ","), summary.totals.net_after_urssaf.toFixed(2).replace(".", ","), ""]);
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `comptabilite_${summary.year}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  if (loading || !summary) {
    return <div className="flex items-center gap-2 text-white/55"><Loader2 className="animate-spin" /> Chargement…</div>;
  }

  const isQuarterDeclareable = (q) => {
    const today = new Date();
    const start = new Date(q.declaration_window.start);
    return today >= start;
  };

  return (
    <div data-testid="admin-accounting">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Comptabilité</p>
          <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Récap <span className="italic gold-text">comptable.</span></h1>
          <p className="text-white/60 text-sm mt-3 max-w-2xl">
            Chiffre d'affaires encaissé, cotisations URSSAF dues, échéances trimestrielles.
            Calcul basé sur les factures payées + factures d'acompte reçues.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select className="input-luxe w-auto" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="admin-accounting-year">
            {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map((y) => <option key={y}>{y}</option>)}
          </select>
          <button onClick={exportCsv} data-testid="admin-accounting-export" className="btn-ghost-gold inline-flex items-center gap-2">
            <FileDown size={14} strokeWidth={1.5} /> Export CSV
          </button>
        </div>
      </header>

      {/* Top metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <MetricTile icon={TrendingUp} label="CA TTC encaissé" value={fmtEur(summary.totals.ca_ttc)} sub={`Année ${summary.year}`} accent />
        <MetricTile icon={Calculator} label="URSSAF dû" value={fmtEur(summary.totals.urssaf_due)} sub={`Taux ${summary.urssaf_rate}% appliqué`} />
        <MetricTile icon={TrendingUp} label="Net après cotisations" value={fmtEur(summary.totals.net_after_urssaf)} sub="Estimation simplifiée" />
      </div>

      {/* Settings */}
      <div className="card-luxe p-5 mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Paramétrage</p>
          <h3 className="font-serif-luxe text-lg mt-2">Taux URSSAF (prestation de services)</h3>
          <p className="text-xs text-white/55 mt-1">Auto-entrepreneur BIC services : 21,2 % · BNC libérale CIPAV : 21,1 % · Ajustez selon votre activité.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="number" step="0.1" min="0" max="50" className="input-luxe w-28" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="admin-accounting-rate" />
          <span className="text-white/70">%</span>
          <button onClick={saveRate} disabled={savingRate} className="btn-gold inline-flex items-center gap-2 disabled:opacity-60">
            {savingRate ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Enregistrer
          </button>
        </div>
      </div>

      {/* Quarters */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {summary.quarters.map((q) => {
          const due = isQuarterDeclareable(q);
          const isCurrentQuarter = (() => {
            const m = new Date().getMonth() + 1;
            return q.months.includes(m) && new Date().getFullYear() === q.year;
          })();
          return (
            <article key={q.quarter} data-testid={`q-${q.quarter}`} className={`card-luxe p-6 relative overflow-hidden ${isCurrentQuarter ? "border-[#DCB457]/60" : ""}`}>
              {isCurrentQuarter && <span className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.18em] bg-[#DCB457] text-black px-2 py-0.5">Trimestre en cours</span>}
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="eyebrow">Trimestre {q.quarter}</p>
                  <h3 className="font-serif-luxe text-xl mt-2">{q.label}</h3>
                </div>
                <span className="font-serif-luxe text-3xl gold-text">{fmtEur(q.ca_ttc)}</span>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                <div className="card-luxe p-3 bg-white/3">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-white/55">URSSAF à déclarer</p>
                  <p className="text-xl font-serif-luxe mt-1">{fmtEur(q.urssaf_due)}</p>
                </div>
                <div className="card-luxe p-3 bg-white/3">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-white/55">Net estimé</p>
                  <p className="text-xl font-serif-luxe mt-1">{fmtEur(q.net_after_urssaf)}</p>
                </div>
              </div>

              <div className={`mt-5 p-3 border ${due ? "border-amber-400/40 bg-amber-500/8" : "border-white/10 bg-white/3"}`}>
                <div className="flex items-start gap-2">
                  {due && <AlertCircle size={14} strokeWidth={1.5} className="text-amber-300 mt-0.5" />}
                  <div className="text-xs">
                    <p className={due ? "text-amber-200" : "text-white/65"}>
                      <strong>Déclaration URSSAF :</strong> du {fmtDate(q.declaration_window.start)} au {fmtDate(q.declaration_window.end)}
                    </p>
                    <p className="text-white/50 mt-0.5">
                      Sur <a href="https://www.autoentrepreneur.urssaf.fr" target="_blank" rel="noopener noreferrer" className="text-[#DCB457] hover:underline">autoentrepreneur.urssaf.fr</a>
                      {due && " — à déclarer dès maintenant si pas encore fait."}
                    </p>
                  </div>
                </div>
              </div>

              {q.invoices.length > 0 && (
                <details className="mt-4 group">
                  <summary className="text-[11px] uppercase tracking-[0.18em] text-white/55 cursor-pointer hover:text-[#DCB457]">
                    {q.invoice_count} facture(s) sur la période
                  </summary>
                  <ul className="mt-3 space-y-1.5 text-xs">
                    {q.invoices.map((inv) => (
                      <li key={inv.id} className="flex items-center justify-between gap-3 py-1.5 border-t border-[#DCB457]/8">
                        <span className="font-mono text-[#DCB457]">{inv.number}</span>
                        <span className="text-white/65 flex-1 truncate">{inv.client_name}</span>
                        <span className="text-white/45 text-[11px]">{fmtDate(inv.paid_at || inv.date)}</span>
                        <span className="text-white/85">{fmtEur(inv.amount)}</span>
                        <button
                          type="button"
                          onClick={() => openEditDate(inv)}
                          title="Modifier la date d'encaissement"
                          data-testid={`edit-paidat-${inv.id}`}
                          className="text-white/50 hover:text-[#DCB457]"
                        >
                          <CalendarClock size={12} strokeWidth={1.5} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </article>
          );
        })}
      </div>

      {editing && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={() => !saving && setEditing(null)}>
          <form onSubmit={saveEditDate} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm bg-[#0a0a0a] border border-[#DCB457]/30 p-6" data-testid="edit-paidat-modal">
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">Date d'encaissement</p>
              <button type="button" onClick={() => setEditing(null)} className="text-white/50 hover:text-white"><X size={14} /></button>
            </div>
            <p className="text-sm text-white/70 mb-1">
              Facture <span className="font-mono text-[#DCB457]">{editing.number}</span>
            </p>
            <p className="text-xs text-white/50 mb-4">{editing.client_name}</p>
            <label className="text-[10px] uppercase tracking-[0.2em] text-white/45">Date effective d'encaissement</label>
            <input
              type="date"
              value={editDate}
              onChange={(e) => setEditDate(e.target.value)}
              required
              autoFocus
              data-testid="paidat-input"
              className="input-luxe mt-1 w-full"
            />
            <p className="text-[11px] text-white/45 mt-3">
              Utilisez ceci pour un acompte perçu <strong>avant la mise en ligne</strong>
              &nbsp;— la date impacte le trimestre URSSAF.
            </p>
            <div className="flex gap-2 mt-5">
              <button type="submit" disabled={saving || !editDate} data-testid="paidat-submit" className="btn-gold flex-1 py-2 text-sm disabled:opacity-60">
                {saving ? "…" : "Enregistrer"}
              </button>
              <button type="button" onClick={() => setEditing(null)} disabled={saving} className="border border-white/20 text-white/65 hover:bg-white/5 px-4 py-2 text-sm">Annuler</button>
            </div>
          </form>
        </div>,
        document.body
      )}
      <RentalAccountingDocs year={year} />
    </div>
  );
}

function MetricTile({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className={`card-luxe p-5 ${accent ? "border-[#DCB457]/45" : ""}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="eyebrow">{label}</p>
        <Icon size={16} strokeWidth={1.3} className="text-[#DCB457]" />
      </div>
      <p className="font-serif-luxe text-3xl gold-text">{value}</p>
      <p className="text-[11px] text-white/45 mt-1">{sub}</p>
    </div>
  );
}
